/**
 * Central type exports for Wraps CLI
 * This file maintains backwards compatibility while supporting multi-service architecture
 */

// Re-export CDN types
export type {
  CdnConfigPreset,
  CdnDestroyOptions,
  CdnFeatureCostBreakdown,
  CdnInitOptions,
  CdnRetention,
  CdnStackConfig,
  CdnStackOutputs,
  CdnStatusOptions,
  CdnUpgradeOptions,
  CdnVerifyOptions,
  CloudFrontPriceClass,
  GeoRestriction,
  WrapsCdnConfig,
  WrapsCdnFeature,
  WrapsCdnFeatureMetadata,
} from "./cdn.js";
// Re-export email types
export type {
  AdditionalDomain,
  AlertConfig,
  AlertSeverity,
  AlertThresholds,
  DomainPurpose,
  EmailAgentCreateOptions,
  EmailAgentKillOptions,
  EmailAgentListOptions,
  EmailConfigOptions,
  EmailConfigPreset,
  EmailConnectOptions,
  EmailDomainsConfigOptions,
  EmailFeatureCostBreakdown,
  EmailInboundAddOptions,
  EmailInboundDestroyOptions,
  EmailInboundInitOptions,
  EmailInboundRemoveOptions,
  EmailInboundStatusOptions,
  EmailInboundTestOptions,
  EmailInboundVerifyOptions,
  EmailInitOptions,
  EmailReplyDecodeOptions,
  EmailReplyDestroyOptions,
  EmailReplyInitOptions,
  EmailReplyRotateOptions,
  EmailReplyStatusOptions,
  EmailRestoreOptions,
  EmailStackConfig,
  EmailStackOutputs,
  EmailTestOptions,
  EmailUpdateOptions,
  EmailUpgradeOptions,
  EmailVerifyOptions,
  InboundDomain,
  SESEventType,
  SESReceivingRegion,
  SuppressionReason,
  WrapsEmailConfig,
  WrapsEmailFeature,
  WrapsEmailFeatureMetadata,
} from "./email.js";
export { DEFAULT_ALERT_THRESHOLDS, SES_RECEIVING_REGIONS } from "./email.js";
// Re-export license types
export type { LicenseGenerateOptions } from "./license.js";
// Re-export selfhost types
export type {
  SelfhostConfig,
  SelfhostEnvOptions,
  SelfhostLogsOptions,
  SelfhostStatusOptions,
} from "./selfhost.js";
// Re-export shared types
export type {
  ArchiveRetention,
  ConsoleOptions,
  DashboardOptions,
  DestroyOptions,
  FeatureCost,
  PlatformConnectOptions,
  PlatformDisconnectOptions,
  Provider,
  ServiceType,
  StatusOptions,
  UpdateRoleOptions,
} from "./shared.js";
// Re-export SMS types
export type {
  PhoneNumberType,
  SMSConfigPreset,
  SMSConnectOptions,
  SMSDestroyOptions,
  SMSEventType,
  SMSFeatureCostBreakdown,
  SMSInitOptions,
  SMSStackConfig,
  SMSStackOutputs,
  SMSStatusOptions,
  SMSTestOptions,
  SMSUpdateOptions,
  SMSUpgradeOptions,
  SMSVerifyNumberOptions,
  SMSVerifyOptions,
  WrapsSMSConfig,
  WrapsSMSFeature,
  WrapsSMSFeatureMetadata,
} from "./sms.js";

/**
 * Command options for `wraps email plan` — detect and (with `--set`) change
 * the account's SES pricing plan. Declared directly here rather than in
 * `email.ts` since it has no other sibling exports to live alongside; shape
 * follows `StatusOptions` above.
 */
export type EmailPlanOptions = {
  account?: string;
  region?: string;
  /** Pricing plan value to switch to (validated against `SES_PRICING_PLANS`). */
  set?: string;
  /** Override the estimated monthly send volume used for cost comparison. */
  volume?: string;
  /** Skip the confirmation prompt when mutating with `--set`. */
  yes?: boolean;
  json?: boolean;
};

/**
 * Command options for `wraps email vdm` — show, and (with `--enable`/
 * `--disable`) switch, Virtual Deliverability Manager for the account.
 * Shape follows `EmailPlanOptions` above.
 */
export type EmailVdmOptions = {
  account?: string;
  region?: string;
  /** Turn VDM on. Mutually exclusive with `disable`. */
  enable?: boolean;
  /** Turn VDM off. Mutually exclusive with `enable`. */
  disable?: boolean;
  /** Tri-state: read through `resolveNegatableFlag` to distinguish "off" from "not mentioned". */
  engagement?: boolean;
  /** Tri-state: read through `resolveNegatableFlag` to distinguish "off" from "not mentioned". */
  optimizedDelivery?: boolean;
  /** Skip the confirmation prompt when mutating. */
  yes?: boolean;
  json?: boolean;
};

/**
 * Command options for `wraps email production-access` — show the account's
 * SES production-access / review state and (with `--request`) file the
 * request. Shape follows `EmailPlanOptions` above.
 */
export type EmailProductionAccessOptions = {
  account?: string;
  region?: string;
  /** File the request (mutating). Read-only without it. */
  request?: boolean;
  /** Required by AWS when requesting: the sender's website URL. */
  website?: string;
  /** Required by AWS when requesting: `transactional` or `marketing`. */
  mailType?: string;
  /** Up to 4 additional contact emails, comma-separated. */
  contact?: string;
  /** Skip the confirmation prompt when mutating with `--request`. */
  yes?: boolean;
  json?: boolean;
};

/**
 * Command options for `wraps email agent policy` — change an agent's send
 * caps or allowlist after creation. Declared directly here rather than in
 * `email.ts`, matching `EmailPlanOptions` above.
 */
export type EmailAgentPolicyOptions = {
  name?: string;
  maxPerHour?: number;
  maxPerDay?: number;
  allowRecipient?: string[];
  allowDomain?: string[];
  clearAllowlist?: boolean;
  token?: string;
  json?: boolean;
};

// Backwards compatibility aliases (deprecated, use specific types instead)
// Import specific types for legacy aliases
import type {
  EmailConfigPreset,
  EmailConnectOptions,
  EmailFeatureCostBreakdown,
  EmailInitOptions,
  EmailStackOutputs,
  EmailUpdateOptions,
  EmailUpgradeOptions,
  EmailVerifyOptions,
  WrapsEmailFeature,
  WrapsEmailFeatureMetadata,
} from "./email.js";

/** @deprecated Use EmailConfigPreset instead */
export type ConfigPreset = EmailConfigPreset;

/** @deprecated Use EmailFeatureCostBreakdown instead */
export type FeatureCostBreakdown = EmailFeatureCostBreakdown;

/** @deprecated Use EmailStackOutputs instead */
export type StackOutputs = EmailStackOutputs;

/** @deprecated Use EmailInitOptions instead */
export type InitOptions = EmailInitOptions;

/** @deprecated Use WrapsEmailFeature instead */
export type WrapsFeature = WrapsEmailFeature;

/** @deprecated Use WrapsEmailFeatureMetadata instead */
export type WrapsFeatureMetadata = WrapsEmailFeatureMetadata;

/** @deprecated Use EmailConnectOptions instead */
export type ConnectOptions = EmailConnectOptions;

/** @deprecated Use EmailVerifyOptions instead */
export type VerifyOptions = EmailVerifyOptions;

/** @deprecated Use EmailUpgradeOptions instead */
export type UpgradeOptions = EmailUpgradeOptions;

/** @deprecated Use EmailUpdateOptions instead */
export type UpdateOptions = EmailUpdateOptions;
